<template>
  <div>
    <BContainer v-if="pending" class="mt-4">
      <BSpinner type="grow" :label="t('Loading')" />
    </BContainer>
    <BContainer v-else class="mt-4 mb-4">
      <h1 class="mb-4">{{ t('Contacts') }}</h1>
      <BRow>
        <BCol
          v-for="c in contacts"
          :key="c.id"
          class="contact_card mb-5"
          md="6"
        >
          <BRow>
            <BCol v-if="c.photo" cols="4">
              <BImg :src="c.photo" fluid :alt="c.name || c.title" />
            </BCol>
            <BCol>
              <div class="title">{{ c.title }}</div>
              <div v-if="c.name" class="name">{{ c.name }}</div>
              <div
                v-if="c.description"
                class="description mt-1"
                v-html="c.description"
              />
              <div class="mt-2">
                <div v-if="c.phone">
                  <Icon name="fa6-solid:phone" />
                  <a :href="`tel:${c.phone}`">{{ c.phone }}</a>
                </div>
                <div v-if="c.email">
                  <Icon name="fa6-regular:envelope" />
                  <a :href="`mailto:${c.email}`">{{ c.email }}</a>
                </div>
                <div v-if="c.location">
                  <Icon name="fa6-solid:location-dot" />
                  {{ c.location }}
                </div>
                <div class="mt-3 contact_actions">
                  <BButton
                    v-if="c.link"
                    variant="outline-dark"
                    target="_blank"
                    rel="noopener noreferrer"
                    :href="c.link"
                  >
                    {{ c.link_button_text }}
                  </BButton>
                  <BButton
                    v-if="c.whatsapp_link"
                    variant="outline-dark"
                    target="_blank"
                    rel="noopener noreferrer"
                    :href="c.whatsapp_link"
                  >
                    <Icon name="fa6-brands:whatsapp" class="me-1" />
                    {{ t('WhatsApp') }}
                  </BButton>
                  <BButton
                    v-if="c.telegram_link"
                    variant="outline-dark"
                    target="_blank"
                    rel="noopener noreferrer"
                    :href="c.telegram_link"
                  >
                    <Icon name="fa6-brands:telegram" class="me-1" />
                    {{ t('Telegram') }}
                  </BButton>
                </div>
              </div>
            </BCol>
          </BRow>
        </BCol>
      </BRow>
    </BContainer>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { definePageMeta, useAsyncData, useHead, useNuxtApp } from '#imports'
import { useI18n } from 'vue-i18n'

interface VipContact {
  id: number
  title: string
  name?: string | null
  description?: string | null
  photo?: string | null
  phone?: string | null
  email?: string | null
  location?: string | null
  link?: string | null
  link_button_text?: string | null
  whatsapp_link?: string | null
  telegram_link?: string | null
}

const { t, locale } = useI18n()

const { $apiFetch } = useNuxtApp() as unknown as {
  $apiFetch: (path: string, opts?: Record<string, unknown>) => Promise<VipContact[]>
}

const { data: contactsData, pending } = await useAsyncData(
  'vip-contacts',
  async () => {
    try {
      return await $apiFetch(`/${locale.value}/api/vip/contacts/`)
    } catch (e) {
      console.error(e)
      return [] as VipContact[]
    }
  },
  { watch: [locale] },
)

const contacts = computed(() => contactsData.value ?? [])

useHead(() => ({
  title: t('Contacts'),
}))

definePageMeta({
  layout: 'vip',
  auth: 'guest',
})
</script>

<i18n lang="json">
{
  "en": {
    "Loading": "Loading",
    "Contacts": "Contacts",
    "WhatsApp": "WhatsApp",
    "Telegram": "Telegram"
  },
  "ru": {
    "Loading": "Загрузка",
    "Contacts": "Контакты",
    "WhatsApp": "WhatsApp",
    "Telegram": "Telegram"
  }
}
</i18n>

<style lang="scss" scoped>
.contact_card {
  border: 0;

  .title {
    font-size: 1.4rem;
    font-weight: bold;
    line-height: 1.6rem;
  }

  .description {
    white-space: pre-wrap;
    color: gray;
    font-size: 0.9rem;
  }

  .contact_actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }
}
</style>
